import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Plus, Edit, Trash2, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import CpfCnpjInput from '../components/CpfCnpjInput';
import { mensagemCpfCnpj } from '../utils/validators';

const empty = { nome: '', cpf_cnpj: '', tel: '', whatsapp: '', email: '', end: '', num: '', cep: '', cidade: '', uf: '', obs: '', nome_fantasia: '', razao_social: '', nasc: '' };

export default function Clients() {
  const navigate = useNavigate();
  const { canCreate } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await api.get('/clients/');
      setClients(res.data);
    } catch { toast.error('Erro ao carregar clientes'); }
    finally { setLoading(false); }
  };

  const openModal = (c = null) => {
    if (!c && !canCreate()) {
      toast.error('Assine um plano para cadastrar novos clientes');
      navigate('/subscribe');
      return;
    }
    setEditing(c);
    setForm(c ? { ...c } : empty);
    setShowModal(true);
  };

  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const fetchCep = async (cep) => {
    const clean = cep.replace(/\D/g, '');
    if (clean.length !== 8) return;
    try {
      const res = await fetch(`https://viacep.com.br/ws/${clean}/json/`);
      const data = await res.json();
      if (!data.erro) {
        setForm(p => ({ ...p, end: data.logradouro, cidade: data.localidade, uf: data.uf, bairro: data.bairro }));
      }
    } catch {}
  };

  const save = async () => {
    if (!form.nome) { toast.error('Nome obrigatório'); return; }
    // Valida CPF/CNPJ se preenchido
    if (form.cpf_cnpj) {
      const erro = mensagemCpfCnpj(form.cpf_cnpj);
      if (erro) { toast.error(erro); return; }
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/clients/${editing.id}`, form);
        toast.success('Cliente atualizado');
      } else {
        await api.post('/clients/', form);
        toast.success('Cliente cadastrado');
      }
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Excluir cliente?')) return;
    try {
      await api.delete(`/clients/${id}`);
      toast.success('Cliente excluído');
      load();
    } catch { toast.error('Erro ao excluir'); }
  };

  const filtered = clients.filter(c =>
    !search || [c.nome, c.cpf_cnpj, c.email, c.tel].some(f => f?.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="space-y-5 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold text-white">Clientes</h1>
        <button data-testid="new-client-btn" onClick={() => openModal()}
          className="flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-semibold px-4 py-2 rounded-xl transition-colors">
          <Plus size={17} /> Novo cliente
        </button>
      </div>

      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input className="field-input pl-9" placeholder="Buscar cliente..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-7 h-7 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-zinc-800">
              <tr className="text-xs text-zinc-500">
                <th className="text-left px-4 py-3">Nome</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">CPF/CNPJ</th>
                <th className="text-left px-4 py-3 hidden sm:table-cell">Tel/WhatsApp</th>
                <th className="text-left px-4 py-3 hidden lg:table-cell">Cidade</th>
                <th className="w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/50">
              {filtered.map(c => (
                <tr key={c.id} data-testid={`client-row-${c.id}`} className="table-row-hover">
                  <td className="px-4 py-3">
                    <div className="font-medium text-white">{c.nome}</div>
                    {c.email && <div className="text-xs text-zinc-500">{c.email}</div>}
                  </td>
                  <td className="px-4 py-3 text-zinc-400 hidden md:table-cell">{c.cpf_cnpj || '—'}</td>
                  <td className="px-4 py-3 text-zinc-400 hidden sm:table-cell">{c.whatsapp || c.tel || '—'}</td>
                  <td className="px-4 py-3 text-zinc-400 hidden lg:table-cell">{c.cidade ? `${c.cidade}/${c.uf}` : '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => openModal(c)} className="p-1.5 text-zinc-500 hover:text-orange-400 transition-colors">
                        <Edit size={14} />
                      </button>
                      <button onClick={() => del(c.id)} className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="text-center py-12 text-zinc-500 text-sm">Nenhum cliente encontrado</div>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-lg my-8">
            <div className="flex items-center justify-between p-5 border-b border-zinc-800">
              <h2 className="font-heading font-semibold text-white">{editing ? 'Editar' : 'Novo'} cliente</h2>
              <button onClick={() => setShowModal(false)} className="text-zinc-500 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="field-label">Nome *</label>
                <input data-testid="client-nome-input" className="field-input" value={form.nome} onChange={e => setForm(p => ({ ...p, nome: e.target.value.toUpperCase() }))} />
              </div>
              <div>
                <label className="field-label">CPF/CNPJ</label>
                <CpfCnpjInput
                  value={form.cpf_cnpj}
                  onChange={(v) => setForm(p => ({ ...p, cpf_cnpj: v }))}
                  onlyMode="cpf_cnpj"
                />
              </div>
              <div>
                <label className="field-label">Data nascimento</label>
                <input type="date" className="field-input" value={form.nasc} onChange={set('nasc')} />
              </div>
              <div>
                <label className="field-label">Telefone</label>
                <input type="tel" inputMode="tel" className="field-input" placeholder="(99) 99999-9999" value={form.tel} onChange={set('tel')} />
              </div>
              <div>
                <label className="field-label">WhatsApp</label>
                <input type="tel" inputMode="tel" className="field-input" placeholder="(99) 99999-9999" value={form.whatsapp} onChange={set('whatsapp')} />
              </div>
              <div className="col-span-2">
                <label className="field-label">E-mail</label>
                <input type="email" className="field-input" value={form.email} onChange={set('email')} />
              </div>
              <div>
                <label className="field-label">CEP</label>
                <input className="field-input" value={form.cep}
                  onChange={e => { set('cep')(e); fetchCep(e.target.value); }} />
              </div>
              <div>
                <label className="field-label">Número</label>
                <input className="field-input" value={form.num} onChange={set('num')} />
              </div>
              <div className="col-span-2">
                <label className="field-label">Endereço</label>
                <input className="field-input" value={form.end} onChange={e => setForm(p => ({ ...p, end: e.target.value.toUpperCase() }))} />
              </div>
              <div>
                <label className="field-label">Cidade</label>
                <input className="field-input" value={form.cidade} onChange={e => setForm(p => ({ ...p, cidade: e.target.value.toUpperCase() }))} />
              </div>
              <div>
                <label className="field-label">UF</label>
                <input className="field-input" maxLength={2} value={form.uf} onChange={e => setForm(p => ({ ...p, uf: e.target.value.toUpperCase() }))} />
              </div>
              <div className="col-span-2">
                <label className="field-label">Observações</label>
                <textarea className="field-input min-h-[60px]" value={form.obs} onChange={set('obs')} />
              </div>
            </div>
            <div className="p-5 border-t border-zinc-800 flex justify-end gap-3">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-zinc-400 hover:text-white border border-zinc-700 rounded-xl transition-colors">Cancelar</button>
              <button data-testid="save-client-btn" onClick={save} disabled={saving}
                className="bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-white font-semibold px-5 py-2 rounded-xl transition-colors text-sm">
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
